import { checkPermissions, permissionsByEntity, useAuth } from '@cmg/auth';
import {
  Alert,
  Button,
  Section,
  SnackbarManager,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSkeleton,
  Typography,
} from '@cmg/design-system';
import type { FormikHelpers } from 'formik';
import { FormikProvider, useFormik } from 'formik';
import React, { useCallback, useMemo, useState } from 'react';

import { DeleteFirmInvestorContactDialog } from './DeleteFirmInvestorContactDialog';
import { FirmInvestorContactRow } from './FirmInvestorContactRow';
import type { FirmInvestorContactFormValues } from './FirmInvestorContactsPanel.model';
import {
  EMPTY_FORM_VALUES,
  getContactValidationSchema,
  getInitialValues,
  getTakenEmails,
  toAdminFirmEmployeeInput,
} from './FirmInvestorContactsPanel.model';
import { useFirmInvestorContacts } from './hooks/useFirmInvestorContacts';

type Props = {
  firmId: string;
};

export type Contact = ReturnType<typeof useFirmInvestorContacts>['contacts'][number];

type EditState = { mode: 'none' } | { mode: 'create' } | { mode: 'edit'; contact: Contact };

const INVESTOR_CONTACTS_DIRECTORY_TITLE = 'Investor Contacts Directory';

const FirmInvestorContactsPanel: React.FC<Props> = ({ firmId }) => {
  const { userPermissions } = useAuth();
  const canEdit = checkPermissions(userPermissions, [permissionsByEntity.Firm.FULL]);

  const { contacts, totalCount, loading, error, createContact, updateContact, deleteContact } =
    useFirmInvestorContacts(firmId);

  const [editState, setEditState] = useState<EditState>({ mode: 'none' });
  const [isSaving, setIsSaving] = useState(false);
  const [contactToDelete, setContactToDelete] = useState<Contact | null>(null);

  const isRowEditing = editState.mode !== 'none';

  const actions = useMemo(
    () =>
      canEdit
        ? [
            <Button
              key="add-contact"
              variant="contained"
              color="success"
              disabled={isRowEditing}
              onClick={() => setEditState({ mode: 'create' })}
            >
              Add Contact
            </Button>,
          ]
        : undefined,
    [canEdit, isRowEditing]
  );

  const handleSave = useCallback(
    async (
      values: FirmInvestorContactFormValues,
      formikHelpers: FormikHelpers<FirmInvestorContactFormValues>
    ) => {
      const input = toAdminFirmEmployeeInput(values);
      setIsSaving(true);
      try {
        if (editState.mode === 'edit') {
          await updateContact(editState.contact.id, input);
          SnackbarManager.success('Investor contact updated.');
        } else {
          await createContact(input);
          SnackbarManager.success('Investor contact added.');
        }
        setEditState({ mode: 'none' });
        formikHelpers.resetForm();
      } catch {
        SnackbarManager.error('Failed to save investor contact.');
      } finally {
        setIsSaving(false);
      }
    },
    [editState, updateContact, createContact]
  );

  const formik = useFormik<FirmInvestorContactFormValues>({
    initialValues:
      editState.mode === 'edit' ? getInitialValues(editState.contact) : EMPTY_FORM_VALUES,
    enableReinitialize: true,
    validationSchema: getContactValidationSchema(
      getTakenEmails(contacts, editState.mode === 'edit' ? editState.contact.id : undefined)
    ),
    onSubmit: handleSave,
  });

  const handleCancel = useCallback(() => {
    setEditState({ mode: 'none' });
    formik.resetForm();
  }, [formik]);

  const handleConfirmDelete = useCallback(async () => {
    if (contactToDelete) {
      try {
        await deleteContact(contactToDelete.id);
        SnackbarManager.success('Investor contact deleted.');
      } catch {
        SnackbarManager.error('Failed to delete investor contact.');
      }
    }
    setContactToDelete(null);
  }, [contactToDelete, deleteContact]);

  if (loading) {
    return (
      <Section title={INVESTOR_CONTACTS_DIRECTORY_TITLE}>
        <TableSkeleton numOfColumns={canEdit ? 6 : 5} />
      </Section>
    );
  }

  if (error) {
    return (
      <Section title={INVESTOR_CONTACTS_DIRECTORY_TITLE}>
        <Alert severity="error">{error.message}</Alert>
      </Section>
    );
  }

  return (
    <Section title={INVESTOR_CONTACTS_DIRECTORY_TITLE} px={4} actions={actions}>
      <FormikProvider value={formik}>
        <TableContainer>
          <Table aria-label="Investor Contacts Directory">
            <TableHead>
              <TableRow>
                <TableCell>Full Name</TableCell>
                <TableCell>Contact Email</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Note</TableCell>
                <TableCell>Status</TableCell>
                {canEdit && <TableCell sx={{ width: 96, whiteSpace: 'nowrap' }}>Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {contacts.length === 0 && editState.mode !== 'create' && (
                <TableRow>
                  <TableCell colSpan={canEdit ? 6 : 5} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No records available
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {contacts.map(contact => (
                <FirmInvestorContactRow
                  key={contact.id}
                  contact={contact}
                  isEditing={editState.mode === 'edit' && editState.contact.id === contact.id}
                  isSaving={isSaving}
                  canEdit={canEdit}
                  actionsDisabled={isRowEditing}
                  onEdit={() => setEditState({ mode: 'edit', contact })}
                  onDelete={() => setContactToDelete(contact)}
                  onSave={() => formik.handleSubmit()}
                  onCancel={handleCancel}
                />
              ))}
              {editState.mode === 'create' && (
                <FirmInvestorContactRow
                  contact={null}
                  isEditing={true}
                  isSaving={isSaving}
                  canEdit={canEdit}
                  actionsDisabled={false}
                  onSave={() => formik.handleSubmit()}
                  onCancel={handleCancel}
                />
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </FormikProvider>
      {totalCount > contacts.length && (
        <Typography variant="caption" color="text.secondary">
          Showing {contacts.length} of {totalCount} contacts
        </Typography>
      )}
      <DeleteFirmInvestorContactDialog
        open={contactToDelete !== null}
        contact={contactToDelete}
        onClose={() => setContactToDelete(null)}
        onConfirm={handleConfirmDelete}
      />
    </Section>
  );
};

export default FirmInvestorContactsPanel;
